import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import AppShell from '@/components/AppShell';
import { Bell, Heart, Sprout, BellOff, CheckCheck, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';

const ICONS = {
  new_follower: Heart,
  new_product: Sprout,
};

const ICON_BG = {
  new_follower: 'bg-[#B91C1C]/10 text-[#B91C1C]',
  new_product: 'bg-[#15803D]/10 text-[#15803D]',
};

function timeAgo(iso) {
  if (!iso) return '';
  const diffSec = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (diffSec < 60) return 'just now';
  const m = Math.floor(diffSec / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

export default function NotificationsPage() {
  const navigate = useNavigate();
  const { api } = useAuth();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const [marking, setMarking] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/notifications', { params: { limit: 100 } });
      setItems(data.notifications || []);
      setUnread(data.unread_count || 0);
    } catch {} finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const handleOpen = async (n) => {
    if (!n.read) {
      try {
        await api.post(`/notifications/${n.id}/read`);
        setUnread((u) => Math.max(0, u - 1));
        setItems(prev => prev.map(x => x.id === n.id ? { ...x, read: true } : x));
      } catch {}
    }
    if (n.type === 'new_follower' && n.actor_id) {
      navigate(`/farmer/${n.actor_id}`);
    } else if (n.link?.startsWith('/produce/')) {
      navigate(n.link);
    }
  };

  const markAllRead = async () => {
    if (unread === 0) return;
    setMarking(true);
    try {
      await api.post('/notifications/read-all');
      setItems(prev => prev.map(x => ({ ...x, read: true })));
      setUnread(0);
    } catch {} finally {
      setMarking(false);
    }
  };

  return (
    <AppShell
      hideBell
      headerLeft={
        <button
          type="button"
          data-testid="notifications-back-btn"
          onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/feed'))}
          className="w-9 h-9 rounded-full bg-[#EFF2F7] flex items-center justify-center active:scale-95 transition-transform"
          aria-label="Go back"
        >
          <ArrowLeft className="w-4 h-4 text-[#0B1620]" />
        </button>
      }
      title={
        <div className="flex items-center gap-2">
          <Bell className="w-5 h-5 text-[#0B4F3F]" />
          <span>Notifications</span>
          {unread > 0 && (
            <span className="text-[10px] font-bold text-[#F6F8FB] bg-[#B91C1C] rounded-full px-1.5 py-0.5">
              {unread}
            </span>
          )}
        </div>
      }
      headerRight={
        unread > 0 ? (
          <Button
            data-testid="mark-all-read-btn"
            onClick={markAllRead}
            disabled={marking}
            size="sm"
            variant="ghost"
            className="h-8 text-xs text-[#0B4F3F] hover:bg-[#0B4F3F]/5 px-2"
          >
            <CheckCheck className="w-3.5 h-3.5 mr-1" />
            Mark all read
          </Button>
        ) : null
      }
    >
      <div className="p-4 space-y-2" data-testid="notifications-page">
        {loading ? (
          Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-16 bg-[#F6F8FB] rounded-xl border border-[#E4E8EF] animate-pulse" />
          ))
        ) : items.length === 0 ? (
          <div className="text-center py-16">
            <BellOff className="w-10 h-10 mx-auto text-[#5B6675]/30 mb-3" />
            <p className="text-sm text-[#5B6675]">You're all caught up!</p>
            <p className="text-xs text-[#5B6675]/70 mt-1">
              Like farmers to get notified about their new produce.
            </p>
          </div>
        ) : (
          items.map((n) => {
            const Icon = ICONS[n.type] || Bell;
            return (
              <button
                key={n.id}
                data-testid={`notification-${n.id}`}
                onClick={() => handleOpen(n)}
                className={`w-full text-left bg-[#F6F8FB] rounded-xl border p-3 flex items-start gap-3 active:scale-[0.99] transition-transform ${
                  n.read ? 'border-[#E4E8EF]' : 'border-[#0B4F3F]/30 bg-[#0B4F3F]/[0.02]'
                }`}
              >
                <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${ICON_BG[n.type] || 'bg-[#EFF2F7] text-[#5B6675]'}`}>
                  <Icon className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm leading-snug ${n.read ? 'text-[#5B6675]' : 'text-[#0B1620] font-semibold'}`}>
                    {n.title}
                  </p>
                  {n.body && (
                    <p className="text-xs text-[#5B6675] mt-0.5 line-clamp-2">{n.body}</p>
                  )}
                  <p className="text-[10px] text-[#5B6675]/70 mt-1">{timeAgo(n.created_at)}</p>
                </div>
                {!n.read && (
                  <span className="w-2 h-2 rounded-full bg-[#0B4F3F] mt-2 flex-shrink-0" />
                )}
              </button>
            );
          })
        )}
      </div>
    </AppShell>
  );
}
